// src/services/api/permisosApi.ts
import api from '../../utils/axios';
import { toast } from '../../utils/toast';
import { useState, useCallback } from 'react';
// Interfaces
interface Modulo {
  Id: number;
  Nombre: string;
  Ruta: string;
  Icono?: string;
  Orden: number;
  Activo: boolean;
}

interface AccionAutorizable {
  Id: number;
  Codigo: string;
  Nombre: string;
  Descripcion?: string;
  Modulo: string;
  Activo: boolean;
}

interface RolModulo {
  id: number;
  nombre: string;
  ruta: string;
  icono?: string;
  orden: number;
  activo: boolean;
}

interface PerfilAccion {
  id: number;
  codigo: string;
  nombre: string;
  descripcion?: string;
  modulo: string;
  puedeAutorizar: boolean;
}

interface AutorizacionPendiente {
  id: number;
  codigo: string;
  accion: {
    codigo: string;
    nombre: string;
    modulo: string;
  };
  solicitante: {
    id: number;
    nombre: string;
    usuario: string;
  };
  datosAccion: any;
  fechaSolicitud: string;
  fechaExpiracion: string;
  tiempoRestante: string;
}

interface SolicitudAutorizacion {
  codigoAccion: string;
  datosAccion: any;
}

interface ValidacionAutorizacion {
  valido: boolean;
  mensaje?: string;
  accion?: any;
  solicitante?: any;
  datosAccion?: any;
}

// Hook principal para gestión de permisos
export const usePermisosApi = () => {
  const [isLoading, setIsLoading] = useState(false);
  const baseUrl = '/permisos';

  const handleError = (error: any) => {
    const message = error.response?.data?.message || 'Ha ocurrido un error';
    toast.error('Error');
  };

  const handleSuccess = (message: string) => {
    toast.success('Éxito');
  };

  // Obtener módulos del usuario actual
  const getModulosUsuario = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get<Modulo[]>(`${baseUrl}/modulos`);
      return response.data;
    } catch (error) {
      handleError(error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Obtener acciones del usuario actual
  const getAccionesUsuario = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get<AccionAutorizable[]>(`${baseUrl}/acciones`);
      return response.data;
    } catch (error) {
      handleError(error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Solicitar autorización
  const solicitarAutorizacion = useCallback(async (solicitud: SolicitudAutorizacion) => {
    setIsLoading(true);
    try {
      const response = await api.post(`${baseUrl}/solicitar-autorizacion`, solicitud);
      handleSuccess('Solicitud de autorización enviada');
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Procesar autorización
  const procesarAutorizacion = useCallback(async (
    codigo: string, 
    autorizar: boolean, 
    observaciones?: string
  ) => {
    setIsLoading(true);
    try {
      const response = await api.post(`${baseUrl}/autorizar/${codigo}`, {
        autorizar,
        observaciones
      });
      handleSuccess(autorizar ? 'Autorización aprobada' : 'Autorización rechazada');
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Validar código de autorización
  const validarAutorizacion = useCallback(async (codigo: string): Promise<ValidacionAutorizacion> => {
    setIsLoading(true);
    try {
      const response = await api.get<ValidacionAutorizacion>(
        `${baseUrl}/validar-autorizacion/${codigo}`
      );
      return response.data;
    } catch (error) {
      handleError(error);
      return { valido: false, mensaje: 'Error al validar' };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Verificar si un código coincide con una factura
  const verificarCodigoFactura = useCallback(async (codigo: string, idVenta: number) => {
    try {
      const response = await api.post(`${baseUrl}/verificar-codigo-factura`, { codigo, idVenta });
      return response.data;
    } catch (error) {
      handleError(error);
      return { valido: false, mensaje: 'Error al verificar' };
    }
  }, []);

  // Obtener estado de una solicitud
  const getEstadoSolicitud = useCallback(async (codigo: string) => {
    try {
      const response = await api.get(`${baseUrl}/estado-solicitud/${codigo}`);
      return response.data;
    } catch (error) {
      return { existe: false };
    }
  }, []);

  return {
    isLoading,
    getModulosUsuario,
    getAccionesUsuario,
    solicitarAutorizacion,
    procesarAutorizacion,
    validarAutorizacion,
    verificarCodigoFactura,
    getEstadoSolicitud,
  };
};

// Hook para gestión administrativa de permisos
export const usePermisosGestionApi = () => {
  const [isLoading, setIsLoading] = useState(false);
  const baseUrl = '/permisos/gestion';

  const handleError = (error: any) => {
    const message = error.response?.data?.message || 'Ha ocurrido un error';
    toast.error('Error');
  };

  const handleSuccess = (message: string) => {
    toast.success('Éxito');
  };

  // ========== MÓDULOS ==========
  const getAllModulos = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get<Modulo[]>(`${baseUrl}/modulos`);
      return response.data;
    } catch (error) {
      handleError(error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createModulo = useCallback(async (data: Partial<Modulo>) => {
    setIsLoading(true);
    try {
      const response = await api.post(`${baseUrl}/modulos`, data);
      handleSuccess('Módulo creado exitosamente');
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateModulo = useCallback(async (id: number, data: Partial<Modulo>) => {
    setIsLoading(true);
    try {
      const response = await api.put(`${baseUrl}/modulos/${id}`, data);
      handleSuccess('Módulo actualizado exitosamente');
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ========== ACCIONES ==========
  const getAllAcciones = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get<AccionAutorizable[]>(`${baseUrl}/acciones`);
      return response.data;
    } catch (error) {
      handleError(error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createAccion = useCallback(async (data: Partial<AccionAutorizable>) => {
    setIsLoading(true);
    try {
      const response = await api.post(`${baseUrl}/acciones`, data);
      handleSuccess('Acción creada exitosamente');
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateAccion = useCallback(async (id: number, data: Partial<AccionAutorizable>) => {
    setIsLoading(true);
    try {
      const response = await api.put(`${baseUrl}/acciones/${id}`, data);
      handleSuccess('Acción actualizada exitosamente');
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ========== ASIGNACIÓN ROLES-MÓDULOS ==========
  const getModulosByRol = useCallback(async (idRol: number) => {
    setIsLoading(true);
    try {
      const response = await api.get<RolModulo[]>(`${baseUrl}/roles/${idRol}/modulos`);
      return response.data;
    } catch (error) {
      handleError(error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  const asignarModulosARol = useCallback(async (idRol: number, modulosIds: number[]) => {
    setIsLoading(true);
    try {
      const response = await api.post(`${baseUrl}/roles/${idRol}/modulos`, { modulosIds });
      handleSuccess('Módulos asignados exitosamente');
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const quitarModuloDeRol = useCallback(async (idRol: number, idModulo: number) => {
    setIsLoading(true);
    try {
      await api.delete(`${baseUrl}/roles/${idRol}/modulos/${idModulo}`);
      handleSuccess('Módulo removido exitosamente');
    } catch (error) {
      handleError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ========== ASIGNACIÓN PERFILES-ACCIONES ==========
  const getAccionesByPerfil = useCallback(async (idPerfil: number) => {
    setIsLoading(true);
    try {
      const response = await api.get<PerfilAccion[]>(`${baseUrl}/perfiles/${idPerfil}/acciones`);
      return response.data;
    } catch (error) {
      handleError(error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  const asignarAccionesAPerfil = useCallback(async (
    idPerfil: number, 
    accionesIds: number[], 
    puedeAutorizar: boolean
  ) => {
    setIsLoading(true);
    try {
      const response = await api.post(`${baseUrl}/perfiles/${idPerfil}/acciones`, {
        accionesIds,
        puedeAutorizar
      });
      handleSuccess('Acciones asignadas exitosamente');
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const actualizarPermisoAccion = useCallback(async (
    idPerfil: number,
    idAccion: number,
    puedeAutorizar: boolean
  ) => {
    setIsLoading(true);
    try {
      const response = await api.put(`${baseUrl}/perfiles/${idPerfil}/acciones/${idAccion}`, {
        puedeAutorizar
      });
      handleSuccess('Permiso actualizado exitosamente');
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const quitarAccionDePerfil = useCallback(async (idPerfil: number, idAccion: number) => {
    setIsLoading(true);
    try {
      await api.delete(`${baseUrl}/perfiles/${idPerfil}/acciones/${idAccion}`);
      handleSuccess('Acción removida exitosamente');
    } catch (error) {
      handleError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ========== CONSULTAS ==========
  const getMatrizPermisos = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`${baseUrl}/matriz-permisos`);
      return response.data;
    } catch (error) {
      handleError(error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getPermisosUsuario = useCallback(async (idUsuario: number) => {
    setIsLoading(true);
    try {
      const response = await api.get(`${baseUrl}/usuarios/${idUsuario}/permisos`);
      return response.data;
    } catch (error) {
      handleError(error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ========== AUTORIZACIONES ==========
  const getAutorizacionesPendientes = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get<AutorizacionPendiente[]>(
        `${baseUrl}/autorizaciones/pendientes`
      );
      return response.data;
    } catch (error) {
      handleError(error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getHistorialAutorizaciones = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`${baseUrl}/autorizaciones/historial`);
      return response.data;
    } catch (error) {
      handleError(error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    isLoading,
    // Módulos
    getAllModulos,
    createModulo,
    updateModulo,
    // Acciones
    getAllAcciones,
    createAccion,
    updateAccion,
    // Roles-Módulos
    getModulosByRol,
    asignarModulosARol,
    quitarModuloDeRol,
    // Perfiles-Acciones
    getAccionesByPerfil,
    asignarAccionesAPerfil,
    actualizarPermisoAccion,
    quitarAccionDePerfil,
    // Consultas
    getMatrizPermisos,
    getPermisosUsuario,
    getAutorizacionesPendientes,
    getHistorialAutorizaciones
  };
};